import React, { useState, useEffect } from "react";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, ShieldAlert, ShieldCheck, AlertTriangle, CheckCircle2,
    Clock, Search, Plus, Filter, ArrowRight, Loader2, Sparkles,
    Eye, HelpCircle, Layers, Check, ExternalLink, ChevronRight,
    RefreshCw, Terminal, Target, Radio, Activity, Cpu, Zap
} from "lucide-react";

export default function DetectionCoverage() {
    const [tactics, setTactics] = useState([]);
    const [overallScore, setOverallScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const [activeTacticId, setActiveTacticId] = useState("command-and-control");
    const [searchQuery, setSearchQuery] = useState("");
    const [updatingTech, setUpdatingTech] = useState(null);
    const [deployToast, setDeployToast] = useState(null);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchCoverage();
    }, []);

    const fetchCoverage = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/detect/coverage`, { headers });
            const tacticList = res.data.tactics || [];
            setTactics(tacticList);
            setOverallScore(res.data.overallScore || (tacticList.length > 0 ? Math.round(tacticList.reduce((a, b) => a + (b.score || 0), 0) / tacticList.length) : 0));
            if (tacticList.length > 0 && !tacticList.some(t => t.id === activeTacticId)) {
                setActiveTacticId(tacticList[0].id);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load detection coverage:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (techniqueName, newStatus) => {
        setUpdatingTech(techniqueName);
        try {
            const res = await axios.post(
                `${backendURL}/api/detect/coverage/technique-status`,
                { tacticId: activeTacticId, techniqueName, newStatus },
                { headers }
            );
            if (res.data.success) {
                if (res.data.tactics) {
                    setTactics(res.data.tactics);
                } else if (res.data.tactic) {
                    setTactics(prev => prev.map(t => t.id === activeTacticId ? res.data.tactic : t));
                }
                if (res.data.overallScore !== undefined) {
                    setOverallScore(res.data.overallScore);
                }
            }
        } catch (err) {
            console.error("Failed to update status:", err);
        } finally {
            setUpdatingTech(null);
        }
    };

    const handleDeployRule = async (techName) => {
        await handleUpdateStatus(techName, "detectionRules");
        setDeployToast(`QueryTel Autonomous Rule deployed & activated for "${techName}"`);
        setTimeout(() => setDeployToast(null), 3500);
    };

    const activeTactic = tactics.find(t => t.id === activeTacticId) || tactics[0] || {
        id: "command-and-control",
        name: "Command & Control",
        score: 0,
        description: "Adversary attempting to communicate with systems under their control.",
        techniques: { noCoverage: [], acceptableRisk: [], alternativeControl: [], detectionRules: [] }
    };

    // Calculate Spider Radar Coordinates (14 Tactics)
    const numTactics = tactics.length || 14;
    const center = 220;
    const maxRadius = 140;

    const radarPoints = tactics.map((tactic, i) => {
        const angle = (Math.PI * 2 / numTactics) * i - Math.PI / 2;
        const tacticScore = tactic.score || 0;
        const radius = (tacticScore / 100) * maxRadius;
        const x = center + radius * Math.cos(angle);
        const y = center + radius * Math.sin(angle);

        // Label Position
        const labelRadius = maxRadius + 32;
        const lx = center + labelRadius * Math.cos(angle);
        const ly = center + labelRadius * Math.sin(angle);

        return { tactic, angle, x, y, lx, ly };
    });

    const polygonPath = radarPoints.map(p => `${p.x},${p.y}`).join(" ");

    // Filter techniques
    const filterList = (list = []) => {
        if (!searchQuery.trim()) return list;
        return list.filter(item => item.toLowerCase().includes(searchQuery.toLowerCase()));
    };

    // Aggregate statistics across all 14 tactics
    const totalBlindSpots = tactics.reduce((acc, t) => acc + (t.techniques?.noCoverage?.length || 0), 0);
    const totalActiveRules = tactics.reduce((acc, t) => acc + (t.techniques?.detectionRules?.length || 0), 0);
    const totalAlternative = tactics.reduce((acc, t) => acc + (t.techniques?.alternativeControl?.length || 0), 0);
    const totalAcceptedRisk = tactics.reduce((acc, t) => acc + (t.techniques?.acceptableRisk?.length || 0), 0);

    if (loading && tactics.length === 0) {
        return (
            <div className="w-full min-h-[500px] flex flex-col items-center justify-center space-y-4">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                <p className="text-slate-400 text-sm font-mono">Loading real-time MITRE ATT&CK coverage telemetry...</p>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6 pb-12 animate-in fade-in duration-300">
            {/* ─── Toast Feedback ─── */}
            {deployToast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {deployToast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>02</span>
                <span>/</span>
                <span className="text-slate-400">Detection Coverage</span>
            </div>

            {/* ─── Page Title Banner ─── */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h1 className="text-[24px] lg:text-[28px] font-extrabold text-white tracking-tight">
                        See what you are not catching.
                    </h1>
                    <p className="text-[13px] text-slate-400 mt-1 max-w-3xl leading-relaxed">
                        QueryTel maps your detections to the <strong>MITRE ATT&CK</strong> techniques attackers actually use, so coverage gaps stop being invisible. Drill into any tactic to see which techniques have no coverage, which are an acceptable risk, and which are handled by an alternative control.
                    </p>
                </div>

                <div className="flex items-center gap-3 self-start lg:self-auto flex-wrap">
                    <div className="bg-[#111827] border border-white/[0.06] rounded-xl px-4 py-2 flex items-center gap-3">
                        <div className="text-right">
                            <span className="text-[10px] text-slate-500 font-mono uppercase block">Enterprise Coverage</span>
                            <span className="text-[18px] font-black text-emerald-400 font-mono">{overallScore}%</span>
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">
                            <ShieldCheck className="w-5 h-5" />
                        </div>
                    </div>

                    <button
                        onClick={fetchCoverage}
                        disabled={loading}
                        className="p-2.5 bg-white/[0.03] hover:bg-white/[0.08] text-slate-400 hover:text-white rounded-xl border border-white/[0.06] transition-all"
                        title="Refresh Telemetry"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-indigo-400" : ""}`} />
                    </button>
                </div>
            </div>

            {/* ─── Summary Posture KPI Strip ─── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-3.5 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[18px] font-black text-white font-mono">{totalActiveRules}</div>
                        <div className="text-[11px] text-slate-400 font-medium">Active Policy Controls</div>
                    </div>
                </div>

                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-3.5 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
                        <Layers className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[18px] font-black text-white font-mono">{totalAlternative}</div>
                        <div className="text-[11px] text-slate-400 font-medium">Alternative Controls</div>
                    </div>
                </div>

                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-3.5 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[18px] font-black text-white font-mono">{totalAcceptedRisk}</div>
                        <div className="text-[11px] text-slate-400 font-medium">Accepted Risks</div>
                    </div>
                </div>

                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-3.5 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center">
                        <ShieldAlert className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[18px] font-black text-white font-mono">{totalBlindSpots}</div>
                        <div className="text-[11px] text-slate-400 font-medium">Identified Blind Spots</div>
                    </div>
                </div>
            </div>

            {/* ─── Main 2-Panel Workbench Canvas ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                {/* ═══ LEFT PANEL: MITRE ATT&CK Radar / Spider Chart (6 cols) ═══ */}
                <div className="lg:col-span-6 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-sm flex flex-col justify-between min-h-[640px]">
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-3 mb-2">
                        <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-indigo-400" />
                            <h3 className="text-[14px] font-bold text-white">MITRE ATT&CK Posture Radar</h3>
                        </div>
                        <span className="text-[10.5px] font-mono text-slate-500">{tactics.length} Tactics Scored</span>
                    </div>

                    {/* SVG Radar Chart */}
                    <div className="w-full flex items-center justify-center my-auto relative select-none py-2">
                        <svg viewBox="0 0 440 440" className="w-full max-w-[430px] overflow-visible">
                            {/* Concentric Grid Rings (20%, 40%, 60%, 80%, 100%) */}
                            {[0.2, 0.4, 0.6, 0.8, 1.0].map((level, idx) => (
                                <circle
                                    key={idx}
                                    cx={center}
                                    cy={center}
                                    r={maxRadius * level}
                                    fill={level === 1.0 ? "rgba(99, 102, 241, 0.02)" : "none"}
                                    stroke="rgba(255, 255, 255, 0.08)"
                                    strokeWidth="1"
                                    strokeDasharray={level === 1.0 ? "none" : "2,2"}
                                />
                            ))}

                            {/* Radial Axis Lines */}
                            {radarPoints.map((p, idx) => (
                                <line
                                    key={idx}
                                    x1={center}
                                    y1={center}
                                    x2={center + maxRadius * Math.cos(p.angle)}
                                    y2={center + maxRadius * Math.sin(p.angle)}
                                    stroke="rgba(255, 255, 255, 0.06)"
                                    strokeWidth="1"
                                />
                            ))}

                            {/* Filled Radar Data Polygon */}
                            <polygon
                                points={polygonPath}
                                fill="rgba(99, 102, 241, 0.18)"
                                stroke="#818cf8"
                                strokeWidth="2.5"
                                className="transition-all duration-500"
                            />

                            {/* Interactive Tactic Nodes on Vertices */}
                            {radarPoints.map((p, idx) => {
                                const isActive = activeTacticId === p.tactic.id;
                                return (
                                    <g
                                        key={idx}
                                        onClick={() => setActiveTacticId(p.tactic.id)}
                                        className="cursor-pointer group"
                                    >
                                        {/* Outer Halo */}
                                        {isActive && (
                                            <circle
                                                cx={p.x}
                                                cy={p.y}
                                                r="10"
                                                fill="#6366f1"
                                                opacity="0.3"
                                                className="animate-ping"
                                            />
                                        )}

                                        {/* Node Dot */}
                                        <circle
                                            cx={p.x}
                                            cy={p.y}
                                            r={isActive ? "5.5" : "3.5"}
                                            fill={isActive ? "#ffffff" : "#818cf8"}
                                            stroke="#4f46e5"
                                            strokeWidth={isActive ? "2.5" : "1.5"}
                                            className="transition-all duration-200 group-hover:scale-125"
                                        />

                                        {/* External Tactic Label & Score */}
                                        <text
                                            x={p.lx}
                                            y={p.ly}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill={isActive ? "#ffffff" : "#94a3b8"}
                                            fontSize={isActive ? "9.5" : "8"}
                                            fontWeight={isActive ? "bold" : "normal"}
                                            fontFamily="monospace"
                                            className="transition-colors select-none group-hover:fill-indigo-300"
                                        >
                                            {p.tactic.name}
                                        </text>
                                        <text
                                            x={p.lx}
                                            y={p.ly + 9}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill={(p.tactic.score || 0) >= 80 ? "#34d399" : (p.tactic.score || 0) >= 60 ? "#fbbf24" : "#f87171"}
                                            fontSize="7.5"
                                            fontWeight="bold"
                                            fontFamily="monospace"
                                            className="select-none"
                                        >
                                            {p.tactic.score || 0}%
                                        </text>
                                    </g>
                                );
                            })}
                        </svg>
                    </div>

                    {/* Bottom Tactic Pill Selector Bar */}
                    <div className="pt-4 border-t border-white/[0.06] flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
                        {tactics.map(t => (
                            <button
                                key={t.id}
                                onClick={() => setActiveTacticId(t.id)}
                                className={`text-[10px] font-mono px-2.5 py-1 rounded-md whitespace-nowrap transition-all ${
                                    activeTacticId === t.id
                                        ? "bg-indigo-600 text-white font-bold shadow-sm"
                                        : "bg-white/[0.03] text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                {t.name} ({t.score || 0}%)
                            </button>
                        ))}
                    </div>
                </div>

                {/* ═══ RIGHT PANEL: Tactic Drilldown & Gap Breakdown (6 cols) ═══ */}
                <div className="lg:col-span-6 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-sm space-y-5 min-h-[640px]">
                    
                    {/* Tactic Title & Search Bar */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/[0.06] pb-4">
                        <div>
                            <div className="flex items-center gap-2.5">
                                <h2 className="text-[20px] font-black text-white tracking-tight border-b-2 border-indigo-500 pb-0.5 inline-block">
                                    {activeTactic.name}
                                </h2>
                                <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded border ${
                                    (activeTactic.score || 0) >= 80 ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400" :
                                    (activeTactic.score || 0) >= 60 ? "bg-amber-500/15 border-amber-500/30 text-amber-400" :
                                    "bg-rose-500/15 border-rose-500/30 text-rose-400"
                                }`}>
                                    {activeTactic.score || 0}% Coverage
                                </span>
                            </div>
                            <p className="text-[12px] text-slate-400 mt-1">{activeTactic.description}</p>
                        </div>

                        {/* Search Filter */}
                        <div className="flex items-center bg-black/30 border border-white/[0.08] rounded-lg px-2.5 py-1.5 w-full sm:w-44 self-start sm:self-auto">
                            <Search className="w-3.5 h-3.5 text-slate-500 mr-1.5" />
                            <input
                                type="text"
                                placeholder="Filter techniques..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bg-transparent border-none outline-none text-[11px] text-slate-200 placeholder:text-slate-600 w-full"
                            />
                        </div>
                    </div>

                    {/* 4 Categorized Sections */}
                    <div className="space-y-4">
                        
                        {/* 1. NO COVERAGE (Red) */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-rose-400 font-bold text-[12px]">
                                <div className="w-4 h-4 rounded bg-rose-500/20 flex items-center justify-center text-[10px]">
                                    {activeTactic.techniques?.noCoverage?.length || 0}
                                </div>
                                <span>No coverage</span>
                            </div>

                            <div className="space-y-1.5 pl-6">
                                {filterList(activeTactic.techniques?.noCoverage).length === 0 ? (
                                    <p className="text-[11px] text-slate-500 italic">No blind spots in this category.</p>
                                ) : (
                                    filterList(activeTactic.techniques?.noCoverage).map((tech, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2.5 bg-rose-500/[0.04] border border-rose-500/15 rounded-lg text-[12px] hover:border-rose-500/30 transition-all group">
                                            <span className="text-slate-200 font-medium">{tech}</span>
                                            
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleDeployRule(tech)}
                                                    disabled={updatingTech === tech}
                                                    className="flex items-center gap-1 px-2.5 py-1 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white rounded text-[10.5px] font-bold transition-all border border-rose-500/30"
                                                >
                                                    {updatingTech === tech ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        <Plus className="w-3 h-3" />
                                                    )}
                                                    Deploy QueryTel Rule
                                                </button>

                                                <select
                                                    disabled={updatingTech === tech}
                                                    onChange={(e) => handleUpdateStatus(tech, e.target.value)}
                                                    defaultValue=""
                                                    className="bg-[#1E293B] text-slate-200 border border-white/[0.15] text-[11px] font-medium rounded-lg px-2.5 py-1 outline-none cursor-pointer hover:border-indigo-500 hover:bg-[#334155] transition-all"
                                                >
                                                    <option value="" disabled className="bg-[#0F172A] text-slate-400 font-medium">Move to...</option>
                                                    <option value="acceptableRisk" className="bg-[#0F172A] text-amber-400 font-medium">🟡 Acceptable Risk</option>
                                                    <option value="alternativeControl" className="bg-[#0F172A] text-blue-400 font-medium">🔵 Alternative Control</option>
                                                    <option value="detectionRules" className="bg-[#0F172A] text-emerald-400 font-medium">🟢 Detection Rules</option>
                                                </select>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* 2. ACCEPTABLE RISK (Amber) */}
                        <div className="space-y-2 pt-2">
                            <div className="flex items-center gap-2 text-amber-400 font-bold text-[12px]">
                                <div className="w-4 h-4 rounded bg-amber-500/20 flex items-center justify-center text-[10px]">
                                    {activeTactic.techniques?.acceptableRisk?.length || 0}
                                </div>
                                <span>Acceptable risk</span>
                            </div>

                            <div className="space-y-1.5 pl-6">
                                {filterList(activeTactic.techniques?.acceptableRisk).length === 0 ? (
                                    <p className="text-[11px] text-slate-500 italic">No techniques marked as accepted risk.</p>
                                ) : (
                                    filterList(activeTactic.techniques?.acceptableRisk).map((tech, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2.5 bg-amber-500/[0.04] border border-amber-500/15 rounded-lg text-[12px]">
                                            <span className="text-slate-200 font-medium">{tech}</span>
                                            <select
                                                disabled={updatingTech === tech}
                                                onChange={(e) => handleUpdateStatus(tech, e.target.value)}
                                                defaultValue=""
                                                className="bg-[#1E293B] text-slate-200 border border-white/[0.15] text-[11px] font-medium rounded-lg px-2.5 py-1 outline-none cursor-pointer hover:border-indigo-500 hover:bg-[#334155] transition-all"
                                            >
                                                <option value="" disabled className="bg-[#0F172A] text-slate-400 font-medium">Move to...</option>
                                                <option value="noCoverage" className="bg-[#0F172A] text-rose-400 font-medium">🔴 No Coverage</option>
                                                <option value="alternativeControl" className="bg-[#0F172A] text-blue-400 font-medium">🔵 Alternative Control</option>
                                                <option value="detectionRules" className="bg-[#0F172A] text-emerald-400 font-medium">🟢 Detection Rules</option>
                                            </select>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* 3. COVERED BY ALTERNATIVE CONTROL (Blue) */}
                        <div className="space-y-2 pt-2">
                            <div className="flex items-center gap-2 text-blue-400 font-bold text-[12px]">
                                <div className="w-4 h-4 rounded bg-blue-500/20 flex items-center justify-center text-[10px]">
                                    {activeTactic.techniques?.alternativeControl?.length || 0}
                                </div>
                                <span>Covered by Alternative Control</span>
                            </div>

                            <div className="space-y-1.5 pl-6">
                                {filterList(activeTactic.techniques?.alternativeControl).length === 0 ? (
                                    <p className="text-[11px] text-slate-500 italic">No alternative controls assigned.</p>
                                ) : (
                                    filterList(activeTactic.techniques?.alternativeControl).map((tech, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2.5 bg-blue-500/[0.04] border border-blue-500/15 rounded-lg text-[12px]">
                                            <span className="text-slate-200 font-medium">{tech}</span>
                                            <select
                                                disabled={updatingTech === tech}
                                                onChange={(e) => handleUpdateStatus(tech, e.target.value)}
                                                defaultValue=""
                                                className="bg-[#1E293B] text-slate-200 border border-white/[0.15] text-[11px] font-medium rounded-lg px-2.5 py-1 outline-none cursor-pointer hover:border-indigo-500 hover:bg-[#334155] transition-all"
                                            >
                                                <option value="" disabled className="bg-[#0F172A] text-slate-400 font-medium">Move to...</option>
                                                <option value="noCoverage" className="bg-[#0F172A] text-rose-400 font-medium">🔴 No Coverage</option>
                                                <option value="acceptableRisk" className="bg-[#0F172A] text-amber-400 font-medium">🟡 Acceptable Risk</option>
                                                <option value="detectionRules" className="bg-[#0F172A] text-emerald-400 font-medium">🟢 Detection Rules</option>
                                            </select>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* 4. DETECTION RULES (Green) */}
                        <div className="space-y-2 pt-2">
                            <div className="flex items-center gap-2 text-emerald-400 font-bold text-[12px]">
                                <div className="w-4 h-4 rounded bg-emerald-500/20 flex items-center justify-center text-[10px]">
                                    {activeTactic.techniques?.detectionRules?.length || 0}
                                </div>
                                <span>Detection Rules</span>
                            </div>

                            <div className="space-y-1.5 pl-6">
                                {filterList(activeTactic.techniques?.detectionRules).length === 0 ? (
                                    <p className="text-[11px] text-slate-500 italic">No active detection rules.</p>
                                ) : (
                                    filterList(activeTactic.techniques?.detectionRules).map((tech, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2.5 bg-emerald-500/[0.04] border border-emerald-500/15 rounded-lg text-[12px]">
                                            <span className="text-slate-200 font-medium flex items-center gap-2">
                                                <Check className="w-3.5 h-3.5 text-emerald-400" />
                                                {tech}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                                    Active Policy
                                                </span>
                                                <select
                                                    disabled={updatingTech === tech}
                                                    onChange={(e) => handleUpdateStatus(tech, e.target.value)}
                                                    defaultValue=""
                                                    className="bg-[#1E293B] text-slate-200 border border-white/[0.15] text-[11px] font-medium rounded-lg px-2.5 py-1 outline-none cursor-pointer hover:border-indigo-500 hover:bg-[#334155] transition-all"
                                                >
                                                    <option value="" disabled className="bg-[#0F172A] text-slate-400 font-medium">Move to...</option>
                                                    <option value="noCoverage" className="bg-[#0F172A] text-rose-400 font-medium">🔴 No Coverage</option>
                                                    <option value="acceptableRisk" className="bg-[#0F172A] text-amber-400 font-medium">🟡 Acceptable Risk</option>
                                                    <option value="alternativeControl" className="bg-[#0F172A] text-blue-400 font-medium">🔵 Alternative Control</option>
                                                </select>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
}
